import { Controller } from '@hotwired/stimulus'
import { badgeClasses } from '@/utils/badge_classes'

/**
 * Tax Organizer Topbar Progress Controller
 *
 * Shows a compact progress bar in the topbar when the main progress card
 * scrolls out of viewport. Uses IntersectionObserver to detect visibility
 * and MutationObserver to sync progress updates from the main progress bar.
 *
 * Usage:
 *   <div data-controller="tax-organizer--topbar-progress"
 *        data-tax-organizer--topbar-progress-progress-card-value="#progress-card-selector">
 *     <!-- Progress card content -->
 *   </div>
 *
 * Targets:
 *   - progressBar: The main progress bar element to watch for width changes
 *   - progressBadge: The main progress badge to watch for text changes
 */
export default class extends Controller {
  static targets = ['progressBar', 'progressBadge']

  connect() {
    this.topbarSlot = document.getElementById('topbar-page-slot')
    if (!this.topbarSlot) {
      console.warn('Topbar slot not found, sticky progress disabled')
      return
    }

    this.isCompactVisible = false
    this.setupIntersectionObserver()
    this.setupMutationObserver()
  }

  disconnect() {
    this.cleanupObservers()
    this.hideCompactProgress()
  }

  setupIntersectionObserver() {
    // Watch the controller element (progress card container) for viewport visibility
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.hideCompactProgress()
          } else {
            this.showCompactProgress()
          }
        })
      },
      {
        // Trigger when element is completely out of viewport
        threshold: 0,
        // Account for topbar height (70px) so we show compact before card is fully hidden
        rootMargin: '-70px 0px 0px 0px'
      }
    )

    this.intersectionObserver.observe(this.element)
  }

  setupMutationObserver() {
    // Watch for changes to the main progress bar/badge to sync with compact version
    this.mutationObserver = new MutationObserver(() => {
      if (this.isCompactVisible) {
        this.syncProgress()
      }
    })

    // Observe style changes on progress bar (width updates)
    if (this.hasProgressBarTarget) {
      this.mutationObserver.observe(this.progressBarTarget, {
        attributes: true,
        attributeFilter: ['style', 'aria-valuenow']
      })
    }

    // Observe text changes on progress badge
    if (this.hasProgressBadgeTarget) {
      this.mutationObserver.observe(this.progressBadgeTarget, {
        childList: true,
        characterData: true,
        subtree: true
      })
    }
  }

  cleanupObservers() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
  }

  showCompactProgress() {
    if (this.isCompactVisible) return

    const progressValue = this.getCurrentProgress()
    const progressText = this.getCurrentProgressText()

    this.topbarSlot.innerHTML = this.buildCompactProgressHTML(progressValue, progressText)
    this.topbarSlot.classList.remove('d-none')
    this.topbarSlot.classList.add('d-block')
    this.isCompactVisible = true
  }

  hideCompactProgress() {
    if (!this.isCompactVisible) return

    this.topbarSlot.classList.add('d-none')
    this.topbarSlot.classList.remove('d-block')
    this.topbarSlot.innerHTML = ''
    this.isCompactVisible = false
  }

  syncProgress() {
    const progressValue = this.getCurrentProgress()
    const progressText = this.getCurrentProgressText()
    const variant = progressValue >= 100 ? 'success' : 'primary'

    const compactBadge = this.topbarSlot.querySelector('[data-compact-progress-badge]')

    if (compactBadge) {
      compactBadge.textContent = progressText
      compactBadge.className = badgeClasses({ variant, size: 'lg' })
    }
  }

  getCurrentProgress() {
    if (this.hasProgressBarTarget) {
      return parseInt(this.progressBarTarget.getAttribute('aria-valuenow') || '0', 10)
    }
    return 0
  }

  getCurrentProgressText() {
    if (this.hasProgressBadgeTarget) {
      return this.progressBadgeTarget.textContent.trim()
    }
    return '0% Complete'
  }

  buildCompactProgressHTML(progressValue, progressText) {
    const variant = progressValue >= 100 ? 'success' : 'primary'
    return `
      <div class="tax-organizer-topbar-progress" data-test="topbar-progress" style="vertical-align: middle;">
        <span class="${badgeClasses({ variant, size: 'lg' })}" data-compact-progress-badge data-test="topbar-progress-badge">${progressText}</span>
      </div>
    `
  }
}
